import { computed, ref } from 'vue'
import { dateTimeFieldConfig, timeFieldConfig } from './dateFieldPresets'
import { api } from '@/global/services/api'

const TRANSLATION_NAMESPACE = 'road-maintenance/temporary_regulation/work_order_details'

const locationTypeItems = ref([])
let locationTypeItemsRequested = false

function fetchLocationTypeItems () {
  if (locationTypeItemsRequested) {
    return
  }
  locationTypeItemsRequested = true

  api('road-maintenance').get('codebook', { types: ['WO_PLAN_LOCATION_TYPE'] })
    .then(({ data }) => {
      locationTypeItems.value = data?.wo_plan_location_type || []
    })
    .catch((exception) => {
      console.error(exception)
      locationTypeItemsRequested = false
    })
}

// Same crews list/data source as the crew-scheduling screen's crew dropdown.
const crewItems = ref([])
let crewItemsRequested = false

function fetchCrewItems () {
  if (crewItemsRequested) {
    return
  }
  crewItemsRequested = true

  api('road-maintenance').get('crews', { pagination: JSON.stringify({ currentPage: 1, perPage: 200 }) })
    .then(({ data }) => {
      crewItems.value = (data || []).map((crew) => ({ id: crew.id, name: crew.name }))
    })
    .catch((exception) => {
      console.error(exception)
      crewItemsRequested = false
    })
}

const COLUMNS_COUNT = 4

export function useLocationFields (options) {
  const { translate, location: getLocation, onLocationFieldClick } = options || {}

  fetchLocationTypeItems()
  fetchCrewItems()

  const fields = computed(() => {
    // Called (not just read) inside this computed so it's evaluated fresh on
    // every recompute, tracking whichever location object is CURRENTLY
    // assigned to the prop - a plain reference captured once at setup time
    // would go stale the moment the phase's `locations` array gets replaced
    // wholesale (eg. after "refresh data" re-fetches everything), silently
    // freezing `edit` at whatever it was and breaking the fields that should
    // hide while editing.
    const location = typeof getLocation === 'function' ? getLocation() : getLocation

    const allFields = [
      {
        key: 'geolocation_label',
        label: translate('locations.location'),
        editable: false,
        visible: true,
        appendIcon: 'mdi-map-marker-outline',
        // Read-only, but still a click target while editing — forceEditStyle
        // keeps the usual green "editing" look even though the field itself
        // can't be typed into.
        forceEditStyle: !!location?.editing,
        // The save payload sends this as `geolocation_id` - map the backend's
        // validation error key back onto this display field so a "required"
        // error on an unset location actually surfaces here.
        validationPropertyKey: 'geolocation_id',
        onClick: () => {
          if (typeof onLocationFieldClick === 'function') {
            onLocationFieldClick()
          }
        },
        appendIconClick: () => {
          if (typeof onLocationFieldClick === 'function') {
            onLocationFieldClick()
          }
        }
      },
      {
        key: 'type_id',
        label: translate(`${TRANSLATION_NAMESPACE}.type`),
        type: 'autocomplete',
        items: locationTypeItems.value,
        autocomplete_options: {
          custom_display_value: (item) => translate(item?.description || ''),
          allowSearch: false
        },
        editable: true,
        visible: true
      },
      {
        key: 'crew_id',
        label: translate('road-maintenance/temporary_regulation/crew_scheduling.crew_selection'),
        type: 'autocomplete',
        items: crewItems.value,
        autocomplete_options: {
          allowSearch: false
        },
        editable: true,
        visible: true
      },
      {
        ...dateTimeFieldConfig('planned_start', translate(`${TRANSLATION_NAMESPACE}.planned_start`)),
        editable: true,
        visible: true
      },
      {
        ...timeFieldConfig('planned_duration', translate(`${TRANSLATION_NAMESPACE}.duration`)),
        editable: true,
        visible: true
      },
      {
        // Read-only display of an already formatted datetime string - a plain
        // text field, not a picker, since there is nothing to pick here.
        key: 'actual_start',
        label: translate(`${TRANSLATION_NAMESPACE}.actual_start`),
        editable: false,
        visible: (vm) => !!vm.formData?.id && !vm.edit
      },
      {
        key: 'actual_end',
        label: translate(`${TRANSLATION_NAMESPACE}.actual_end`),
        editable: false,
        visible: (vm) => !!vm.formData?.id && !vm.edit
      },
      {
        key: 'status_label',
        label: translate(`${TRANSLATION_NAMESPACE}.status`),
        editable: false,
        visible: (vm) => !!vm.formData?.id && !vm.edit
      }
    ]

    // FieldsRenderer groups fields by their `row` and pads each row up to
    // `columnsCount` with blank placeholders - fixed row numbers left the
    // location field alone (with 3 blank slots) whenever the other fields on
    // its row were hidden (eg. entering edit mode hides the whole "row 2").
    // Assign rows dynamically, after filtering to only the currently visible
    // fields, so they always pack 4-per-row with no gaps.
    const evalVm = { formData: location, edit: !!location?.editing }

    return allFields
      .filter(field => {
        const { visible } = field
        return typeof visible === 'function' ? visible(evalVm) : !!visible
      })
      .map((field, index) => ({
        ...field,
        row: Math.floor(index / COLUMNS_COUNT),
        visible: true
      }))
  })

  return computed(() => ({
    columnsCount: COLUMNS_COUNT,
    fields: fields.value
  }))
}
